import { Form } from "antd"
import { LoadingRegion } from "components/custom/Loader"
import { Button } from "components"
import { AppSegmentWrapper } from "components/custom/AppSegmentWrapper"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { PermissionsPicker } from "./PermissionsPicker"
import { RoleForm } from "./RoleForm"
import {
  ICustomRoleRequest,
  IGetAdmins,
  IGetAdminsRes,
  IRole,
  IRoleRes,
} from "./interface/interface"
import { useMutation, useQuery } from "@tanstack/react-query"
import { useAppNotificationStore } from "stores"
import { LoadingButton } from "components/buttons/LoadingButton"
import usePermissions from "hooks/usePermissions"
import SegmentWrapper from "components/custom/SegmentWrapper"
import { onQueryError } from "utils/utils"
import { ROLE_ENDPOINTS, roleService } from "services/roleService"
import { COMPANY_ENDPOINTS, companyService } from "services/companyService"
import { IAllAdminsRes } from "stores/setting/permissions"

const ViewRoleDetailPage = () => {
  const { roleId } = useParams()

  const {
    data: role,
    isLoading,
    refetch,
  } = useQuery<IRoleRes>({
    queryKey: [ROLE_ENDPOINTS.VIEW_EDIT, roleId],
    queryFn: async () => roleService.viewEdit(roleId || ""),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })

  const [form] = Form.useForm()

  const [canEditRole] = usePermissions({
    allowedPermissions: ["can_edit_custom_roles"],
  })

  const [employees, setEmployees] = useState<string[]>([])

  const [isEditMode, setIsEditMode] = useState<boolean>(false)

  const [roleDetail, setRoleDetail] = useState<any>()

  const [formData, setFormData] = useState<ICustomRoleRequest>()

  //@: Local state to manage the selected permissions
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([])

  const { data: admins, isLoading: isGetting } = useQuery<IGetAdminsRes>({
    queryKey: [COMPANY_ENDPOINTS.GET_ADMINS, !role?.data?.is_default],
    queryFn: async () => companyService.getAdmins(!role?.data?.is_default),
    refetchOnWindowFocus: false,
    cacheTime: 24 * 60 * 60 * 1000,
  })
  const { toast } = useAppNotificationStore()

  const navigate = useNavigate()

  const { mutate, isLoading: isUpdating } = useMutation({
    mutationFn: async (data: ICustomRoleRequest) => roleService.update(data),
    onSuccess: (data) => {
      if (data.success) {
        data?.message && toast.success(data?.message)
        refetch()
        setIsEditMode(false)
      }
    },
    onError: onQueryError,
  })

  useEffect(() => {
    setRoleDetail(role?.data)
    setEmployees(role?.data?.roleMembers?.map((x) => x?.nick_name) as string[])
    setSelectedPermissions(
      role?.data?.rolePermissions?.map((x: any) =>
        x?.permission_id?.toString()
      ) as string[]
    )
  }, [role?.data])

  return (
    <DashboardWrapper>
      <LoadingRegion
        spinning={isLoading}
        keepMounted
      >
        <SegmentWrapper
          title="Role Details"
          actionBtn={
            role?.data &&
            !role?.data?.is_default && (
              <Button
                color="primary"
                onClick={() => {
                  if (canEditRole) {
                    setIsEditMode(true)
                  } else {
                    toast.error("You don't have permission to edit this role")
                  }
                }}
                className={`${!isEditMode ? "flex" : "hidden"}`}
              >
                Edit
              </Button>
            )
          }
        >
          <div className="flex flex-col relative h-screen overflow-y-hidden overflow-x-hidden px-4 md:px-6 xl:px-[60px] -mb-10">
            <div className="grid lg:grid-cols-[40%,60%] gap-2 lg:gap-0 grid-cols-1 box-border h-[100%]">
              <RoleForm
                form={form}
                employees={employees}
                setEmployees={setEmployees}
                isEditMode={isEditMode}
                initialData={roleDetail}
                showPeople={!isEditMode}
                isCustomRole={!role?.data?.is_default}
                admins={admins?.data || []}
                roleKey={roleId}
                onUpdateData={(values: any) => {
                  setFormData(values)
                }}
                onSuccess={(status) => {
                  if (status) {
                    refetch()
                  }
                }}
                onFinish={() => {
                  mutate({
                    ...formData,
                    permissions: selectedPermissions.map((permission) =>
                      String(permission)
                    ),
                    assignments: employees?.map((x: string) =>
                      admins?.data
                        ?.find((it: any) => it?.text === x)
                        ?.id?.toString()
                    ),
                  } as ICustomRoleRequest)
                }}
              />

              <PermissionsPicker
                selectedPermissions={selectedPermissions}
                setSelectedPermissions={setSelectedPermissions}
                isEditMode={isEditMode}
              />
            </div>
            {isEditMode && (
              <div className="flex justify-end gap-3 mt-6 border-t pr-3 pt-6 pb-4 absolute bottom-0 right-0 w-full bg-white">
                <Button
                  color="neutral"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>

                <Button
                  color="primary"
                  onClick={(e) => {
                    e.preventDefault()

                    mutate({
                      id: role?.data?.role_id?.toString(),
                      ...formData,
                      permissions: selectedPermissions.map((permission) =>
                        String(permission)
                      ),
                    } as ICustomRoleRequest)
                  }}
                  type="submit"
                  submitting={isUpdating}
                >
                  Save changes
                </Button>
              </div>
            )}
          </div>
        </SegmentWrapper>
      </LoadingRegion>
    </DashboardWrapper>
  )
}

export default ViewRoleDetailPage
